<script setup lang="ts">
import { computed } from 'vue';
import type { QuoteByTypeResult } from '~~/server/services/quote-by-type';
import { TYPE_CAPACITY } from '~~/server/db/apartment-types';
import type { ApartmentTypeId } from '~~/server/db/apartment-types';
import { usePriceFormatter } from '~/composables/usePriceFormatter';

const props = defineProps<{
  type: ApartmentTypeId;
  result: QuoteByTypeResult | undefined;
  searchHref: string;
}>();

const emit = defineEmits<{
  ctaClick: [type: ApartmentTypeId, totalUsd: number, nights: number];
}>();

const { t } = useI18n();
const { format } = usePriceFormatter();

// Free mid-stay cleanings: one after each full 5-day block.
// 5 nights → 0, 6..10 → 1, 11..15 → 2, etc.
const cleaningsIncluded = computed(() => {
  if (!props.result?.available) return 0;
  return Math.max(0, Math.floor((props.result.nights - 1) / 5));
});

function onCtaClick() {
  if (!props.result?.available) return;
  emit('ctaClick', props.type, props.result.totalUsd, props.result.nights);
}
</script>

<template>
  <!-- Available: expandable accordion (closed by default) -->
  <details
    v-if="result?.available"
    class="group"
  >
    <summary class="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none">
      <span class="font-body font-semibold text-sm md:text-base text-slate-900 dark:text-white">
        {{ t(`prices.table.type.${type}`) }}
      </span>
      <span class="flex items-center gap-3">
        <span class="font-display italic text-xl md:text-2xl tabular-nums text-amber-700 dark:text-amber-300">
          {{ format(result.totalUsd) }}
        </span>
        <Icon name="lucide:chevron-down" class="size-4 transition-transform group-open:rotate-180" aria-hidden="true" />
      </span>
    </summary>
    <div class="px-5 pb-5 pt-1 border-t border-slate-100 dark:border-slate-800/80">
      <dl class="space-y-1.5 font-body text-sm text-slate-700 dark:text-slate-200">
        <div class="flex justify-between">
          <dt>
            {{ t('prices.calculator.row.subtotal', {
              nights: result.nights,
              rate: format(Math.round(result.subtotalUsd / result.nights)),
            }) }}
          </dt>
          <dd class="tabular-nums">{{ format(result.subtotalUsd) }}</dd>
        </div>
        <div
          class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
          :class="result.cleaningUsd > 0 ? '' : 'text-emerald-700 dark:text-emerald-400'"
        >
          <dt class="flex items-baseline gap-2 flex-wrap">
            <span v-if="cleaningsIncluded === 0">{{ t('prices.calculator.row.cleaning') }}</span>
            <span
              v-else
              class="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400"
            >
              <Icon name="lucide:sparkles" class="size-3.5" aria-hidden="true" />
              {{ t('prices.calculator.row.cleaningsIncluded', cleaningsIncluded, { named: { n: cleaningsIncluded } }) }}
            </span>
          </dt>
          <dd v-if="result.cleaningUsd > 0" class="tabular-nums">
            {{ format(result.cleaningUsd) }}
          </dd>
          <dd v-else>{{ t('prices.calculator.row.cleaningIncluded') }}</dd>
        </div>
        <div v-if="result.extraGuestUsd > 0" class="flex justify-between">
          <dt>
            {{ t('prices.calculator.row.extraGuest', {
              nights: result.nights,
              rate: format(Math.round(result.extraGuestUsd / result.nights)),
            }) }}
          </dt>
          <dd class="tabular-nums">{{ format(result.extraGuestUsd) }}</dd>
        </div>
        <div class="flex justify-between font-semibold text-base border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
          <dt>{{ t('prices.calculator.row.total') }}</dt>
          <dd class="tabular-nums">{{ format(result.totalUsd) }}</dd>
        </div>
        <div class="flex justify-between text-xs text-slate-500 dark:text-slate-400">
          <dt>{{ t('prices.calculator.row.prepayment') }}</dt>
          <dd class="tabular-nums">{{ format(result.prepaymentUsd) }}</dd>
        </div>
        <div v-if="result.totalCount > 1" class="text-xs text-slate-500 dark:text-slate-400 pt-1">
          {{ t('prices.calculator.row.availableOf', {
            available: result.availableCount,
            total: result.totalCount,
          }) }}
        </div>
      </dl>
      <NuxtLink
        :to="searchHref"
        class="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-slate-900 hover:bg-amber-300 transition"
        @click="onCtaClick"
      >
        {{ t('prices.calculator.row.selectCta') }}
        <Icon name="lucide:arrow-right" class="size-3.5" aria-hidden="true" />
      </NuxtLink>
    </div>
  </details>

  <!-- Unavailable: inline disabled state -->
  <div
    v-else-if="result && !result.available && result.reason !== 'no_listings'"
    class="px-5 py-4 flex items-center justify-between gap-4"
  >
    <span class="font-body font-semibold text-sm md:text-base text-slate-500 dark:text-slate-400">
      {{ t(`prices.table.type.${type}`) }}
    </span>
    <span class="font-body text-xs md:text-sm text-slate-500 dark:text-slate-400">
      <template v-if="result.reason === 'capacity'">
        {{ t('prices.calculator.unavailable.capacity', { capacity: TYPE_CAPACITY[type] }) }}
      </template>
      <template v-else-if="result.reason === 'all_booked'">
        {{ t('prices.calculator.unavailable.all_booked') }}
        <NuxtLink
          :to="searchHref"
          class="ml-2 underline decoration-amber-400 underline-offset-4 hover:text-amber-700 dark:hover:text-amber-300"
        >
          {{ t('prices.calculator.unavailable.all_booked_link') }}
        </NuxtLink>
      </template>
    </span>
  </div>

  <!-- Loading skeleton (result is undefined / quotes not yet fetched) -->
  <div
    v-else-if="!result"
    class="px-5 py-4 flex items-center justify-between gap-4"
  >
    <span class="font-body font-semibold text-sm md:text-base text-slate-300 dark:text-slate-600">
      {{ t(`prices.table.type.${type}`) }}
    </span>
    <span class="h-6 w-24 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse" aria-hidden="true" />
  </div>

  <!-- no_listings: rendered nothing here; parent <li> hides this row via v-if -->
</template>
